import { useState } from "react";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminCodeStep = ({ challengeId, minutes, onDone, onBack, formatError }) => {
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setNote("");
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/verify-code`,
        { challenge_id: challengeId, code, remember_device: remember },
        { withCredentials: true }
      );
      onDone();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError("");
    setNote("");
    try {
      await axios.post(`${API}/auth/resend-code`, { challenge_id: challengeId }, { withCredentials: true });
      setNote("A new code is on its way. Only the latest code will work.");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <form onSubmit={verify} className="mt-10 space-y-8" data-testid="admin-code-form">
      <p className="text-sm text-[#F9F8F4]/70" data-testid="admin-code-sent-note">
        We've emailed a 6-digit code to your inbox. It expires in {minutes} minutes.
      </p>
      <div>
        <label htmlFor="admin-code" className="block text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">
          Sign-in code
        </label>
        <input
          id="admin-code"
          data-testid="admin-code-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          required
          pattern="\d{6}"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="w-full bg-transparent border-b border-white/25 focus:border-[#F9F8F4] outline-none py-3 text-2xl tracking-[0.5em] text-[#F9F8F4] placeholder:text-white/30 transition-[border-color] duration-300"
          placeholder="000000"
        />
      </div>
      <label className="flex items-center gap-3 text-sm text-[#F9F8F4]/70 cursor-pointer">
        <input
          type="checkbox"
          data-testid="admin-remember-device-checkbox"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
          className="h-4 w-4 accent-[#A78558]"
        />
        Remember this device for 5 days
      </label>

      {error && (
        <p data-testid="admin-code-error" className="text-sm text-[#d98a72] border border-[#A78558]/40 px-4 py-3">{error}</p>
      )}
      {note && <p data-testid="admin-code-note" className="text-sm text-[#A78558]">{note}</p>}

      <div className="flex flex-wrap items-center gap-6">
        <button
          data-testid="admin-code-submit"
          type="submit"
          disabled={loading || code.length !== 6}
          className="inline-flex items-center gap-2 rounded-full bg-[#F9F8F4] px-8 py-4 text-xs uppercase tracking-[0.15em] text-[#0A2333] transition-[background-color,transform,opacity] duration-300 hover:bg-white hover:-translate-y-0.5 disabled:opacity-60"
        >
          {loading ? <>Verifying <Loader2 size={15} className="animate-spin" /></> : <>Verify <ArrowRight size={15} /></>}
        </button>
        <button type="button" onClick={resend} data-testid="admin-code-resend"
          className="text-xs uppercase tracking-[0.15em] text-[#F9F8F4]/70 hover:text-[#F9F8F4] transition-colors">
          Resend code
        </button>
        <button type="button" onClick={onBack} data-testid="admin-code-back"
          className="text-xs uppercase tracking-[0.15em] text-[#F9F8F4]/50 hover:text-[#F9F8F4] transition-colors">
          Start over
        </button>
      </div>
    </form>
  );
};
